import { colorByInvoiceStatus } from "./colorHelpers";
import { convertToDate, generateUniqueID } from "./helpers";
import { constructBeneficiaries } from "./beneficiariesAdapter";
import { logoByMerchant } from "./logoHelper";

const getType = invoice => (invoice.invoices?.length > 0 ? "statement" : invoice.type.toLowerCase());
const getUsn = invoice => invoice.additionalFields?.find(f => f.code === "usn")?.value;

const getPaymentDetails = invoice => {
  const { paymentDetails } = invoice;
  if (paymentDetails) {
    const {
      amount,
      currency,
      paymentDateTime,
      paymentFee,
      collector,
      paymentMode,
      totalAmount,
      paymentReference,
      payer
    } = paymentDetails;

    return {
      code: invoice.code,
      type: getType(invoice),
      paymentReference,
      amount,
      fee: paymentFee,
      totalAmount,
      currency,
      paymentMode,
      collector,
      date: convertToDate(paymentDateTime),
      payer
    };
  }
  return null;
};

const getCustomer = (invoice, type = "COMPANY") => invoice.customers?.find(c => c.type === type);

const invoiceGridDataAdaptor = invoice => ({
  ...invoice,
  code: invoice.id,
  tmp: invoice.id,
  tmp1: invoice.id,
  customerName: invoice.customerName,
  type: invoice.type.toLowerCase(),
  amount: invoice.amount,
  color: colorByInvoiceStatus(invoice.status),
  createdAt: convertToDate(invoice.createdAt),
  paidAt: convertToDate(invoice.paidAt),
  invoicesCount: invoice.invoicesCount,
  warning: invoice.warning || false,
  showReconciliationInfo: invoice.pendingReconciliation,
  merchantLogo: logoByMerchant(invoice.merchantCode)?.logo,
  penaltyWarning: invoice.penaltyWarning
});

const getInitials = name => {
  if (typeof name !== "string" || !name) return null;

  const parts = name.toUpperCase().split(" ");

  const [firstName, lastName] = parts;

  return firstName && lastName ? `${firstName[0]}${lastName[0]}` : `${firstName[0]}${firstName[1]}`;
};

const getHistory = events =>
  events
    ? events.map(event => ({
        id: generateUniqueID(),
        status: event.status,
        color: colorByInvoiceStatus(event.status),
        date: new Date(event.occurredAt),
        userName: event?.triggeredBy,
        userInitials: getInitials(event.triggeredBy),
        rejectionReason: event?.statusDetails || null
      }))
    : [];

const invoiceAdaptor = invoice => {
  const customer = getCustomer(invoice, "COMPANY");

  return {
    code: invoice.id,
    company: customer?.name,
    customerName: customer?.name,
    date: convertToDate(invoice.date),
    expirationDate: convertToDate(invoice.expirationDate),
    amount: invoice.amount,
    fee: invoice.fee,
    totalAmount: invoice.totalAmount,
    currency: invoice.currency,
    type: getType(invoice),
    status: invoice.status,
    color: colorByInvoiceStatus(invoice.status),
    beneficiaries: constructBeneficiaries(invoice.beneficiaries),
    agent: getCustomer(invoice, "AGENT"),
    merchantCode: invoice.merchant?.code,
    merchantName: invoice.merchant?.name,
    merchantReference: invoice.merchantReference,
    merchantReferenceUrl: invoice.merchantReferenceURL,
    merchantLogo: logoByMerchant(invoice.merchant?.code)?.logo,
    invoices: invoice.invoices?.map(invoiceAdaptor)?.sort((inv1, inv2) => {
      if (inv1.code > inv2.code) return 1;
      if (inv1.code < inv2.code) return -1;
      return 0;
    }),
    invoicesCount: invoice?.invoices?.length,
    groupInvoice: invoice.groupInvoice,
    paymentDetails: getPaymentDetails(invoice),
    declarant: getCustomer(invoice, "REPRESENTATIVE"),
    warning: invoice.warning || false,
    shared: invoice.shared || false,
    payable: invoice.payable,
    pilotingPhaseValidation: invoice.pilotingPhaseValidation,
    additionalInfo: invoice.additionalInformation?.map(info => ({
      id: generateUniqueID(),
      ...info,
      occurredAt: convertToDate(info.occurredAt)
    })),
    customer,
    description: invoice.description,
    items: invoice.items,
    history: getHistory(invoice.events),
    usn: getUsn(invoice),
    showReconciliationInfo: invoice.showReconciliationInfo,
    justification: invoice.justification,
    penaltyWarning: invoice.penaltyWarning,
    hasAtLeastOneImplementedPenalty: invoice.hasAtLeastOneImplementedPenalty,
    earliestPenaltyImplementationDate: convertToDate(invoice.earliestPenaltyImplementationDate),
    penalty: invoice.penalty
      ? {
          ...invoice.penalty,
          implementationDate: new Date(invoice.penalty.implementationDate)
        }
      : null
  };
};

const invoiceStatusChangeAdaptor = ({
  amount,
  currency,
  date,
  code,
  paymentDetails,
  merchantReference,
  transactionRef,
  payer
}) => ({
  amount,
  currency,
  date,
  cashier: "N/A",
  payer: payer || "N/A",
  payerIban: "N/A",
  paymentMode: "COLLECTED_EXTERNALLY",
  invoiceId: code,
  paymentDateTime: new Date(),
  paymentFee: paymentDetails?.fee || 0,
  paymentReferenceNo: transactionRef,
  transactionId: transactionRef || code
});

export { invoiceGridDataAdaptor, invoiceAdaptor, invoiceStatusChangeAdaptor };
